import { Api } from "./sync/api";
import { Directory } from "./directory";
import { draftedChannels } from "./drafts";
import { SyncEngine, type SocketLike } from "./sync/engine";
import { Store, clearTo, waitingCount } from "./sync/store";
import { navigate, parse } from "./router";
import { chime, ring, unlockSound } from "./sound";
import { popUpsOn } from "./popups";
import { call } from "./huddle";
import type { Capability, Huddle, LinkPreview, Message, Preferences, Principal, QuietHours, Reminder, Shortcut, SidebarLayout, SidebarSection, UUID } from "./sync/types";
import { quietUntil } from "./quiet";
import { browserZone } from "./localtime";
import { setCustom } from "./emoji";
import { clearFaviconDot, setFaviconDot } from "./favicon";
import { setBranding, siteName } from "./brand";
import { setChosen, t, takePicked } from "./i18n";
import { display, type Density, type Layout, type TextSize } from "./display";
import { choose, fromAccount, fromWorkspace, type Appearance } from "./theme";

const tokenKey = "lantern.token";

export function savedToken(): string | null {
  try {
    return localStorage.getItem(tokenKey);
  } catch {
    return null;
  }
}

export function saveToken(token: string | null) {
  try {
    if (token) localStorage.setItem(tokenKey, token);
    else localStorage.removeItem(tokenKey);
  } catch {
    // Private windows may refuse storage; the session lasts until reload.
  }
}

// MFASetupNeeded stops a session from starting: the person's groups need
// two-step verification, and they set it up first.
export class MFASetupNeeded extends Error {
  constructor() {
    super("two-step verification must be set up first");
  }
}

// Session is one signed-in user's connection to the server.
export class Session {
  readonly api: Api;
  readonly store = new Store();
  readonly engine: SyncEngine;
  readonly directory = new Directory(
    [],
    (ids) => this.api.principals({ ids }).then((r) => r.principals),
    () => this.store.update((s) => s.profileBumps++),
  );
  dmMembers = new Map<UUID, UUID[]>();

  constructor(token: string) {
    this.api = new Api("/api/v1", token);
    const scheme = location.protocol === "https:" ? "wss:" : "ws:";
    this.engine = new SyncEngine(this.api, this.store, {
      url: `${scheme}//${location.host}/api/v1/realtime`,
      socket: browserSocket,
      log: (msg, ...args) => console.warn(msg, ...args),
      notify: (m, channel) => this.notify(m, channel.id),
      profile: (id, principal) => void this.refreshPrincipal(id, principal),
      people: (ps) => this.takePeople(ps),
      reminder: (r) => void this.onReminder(r),
      // The workspace changed: renamed, or its custom emoji did.
      workspace: () => {
        void this.api.me().then(
          ({ workspace }) => {
            this.store.update((s) => {
              s.workspace = workspace?.name ?? s.workspace;
              s.recallSeconds = workspace?.recall_seconds ?? s.recallSeconds;
              s.channelCreation = workspace?.channel_creation ?? s.channelCreation;
            });
            if (workspace) fromWorkspace(workspace.theme);
          },
          () => {},
        );
        // Its branding too: the name, accent and logo show at once.
        void this.api.branding().then(
          ({ branding }) => {
            setBranding(branding);
            this.store.update(() => {});
          },
          () => {},
        );
        void this.loadEmoji();
      },
      // Called while the store updates; the toasts change after.
      recalled: (id) => queueMicrotask(() => this.unnotify(id)),
      scheduled: () => void this.loadScheduled(),
      preferences: () => void this.loadPreferences(),
      huddle: (cs, now, before) => this.onHuddle(cs.channel.id, now, before),
      huddleDeclined: (channel, who) => {
        // Declined on another of one's devices: this one stops ringing too.
        if (who === this.store.getSnapshot().me?.id) {
          if (this.store.getSnapshot().ringing?.channel === channel) this.stopRinging();
        } else call.declined(channel, who);
      },
    });
    // A channel one is no longer in takes its call and its ringing with it.
    this.unwatchCall = this.store.subscribe(() => {
      const st = this.store.getSnapshot();
      const now = call.snapshot();
      if (now && !st.channels.has(now.channel)) void call.leaveFor(this);
      if (st.ringing && !st.channels.has(st.ringing.channel)) this.stopRinging();
    });
  }

  private unwatchCall: () => void;

  // onHuddle rings for a call just started in a DM one is in, until one
  // answers or declines it, joins it on another device, it ends, or 30
  // seconds pass; a call that stopped ringing unanswered counts as missed.
  private onHuddle(channelID: UUID, now: Huddle | undefined, before: Huddle | undefined) {
    const st = this.store.getSnapshot();
    const me = st.me?.id ?? "";
    if (st.ringing?.channel === channelID) {
      if (!now) this.stopRinging(true);
      else if (now.participants.includes(me)) this.stopRinging();
      return;
    }
    const kind = st.channels.get(channelID)?.channel.kind;
    // Only a call just started rings, not one caught up on later.
    const fresh = !!now && this.engine.serverNow() - Date.parse(now.started_at) < 60_000;
    if (!now || (kind !== "dm" && kind !== "group_dm") || before || !fresh || this.paused() || now.participants.includes(me)) return;
    this.ringFor(channelID, now.participants[0]);
  }

  private stopRing?: () => void;

  private ringFor(channel: UUID, from: UUID) {
    this.stopRinging();
    this.store.update((s) => (s.ringing = { channel, from }));
    // In a call already, a chime, not a ring over it.
    const stop = call.snapshot() ? (chime(), () => {}) : ring("incoming");
    const timer = setTimeout(() => this.stopRinging(true), 30_000);
    let n: Notification | undefined;
    const front = document.visibilityState === "visible" && document.hasFocus();
    if (!front && typeof Notification !== "undefined" && Notification.permission === "granted") {
      n = new Notification(t("{name} is calling you", { name: this.name(from) }), { body: t("Open Lantern to join the huddle."), tag: "huddle-" + channel });
      n.onclick = () => {
        window.focus();
        navigate(`/c/${channel}`);
        n?.close();
      };
    }
    this.stopRing = () => {
      stop();
      clearTimeout(timer);
      n?.close();
    };
  }

  // stopRinging stops the ringing; missed, it leaves a pop-up saying so.
  private stopRinging(missed = false) {
    this.stopRing?.();
    this.stopRing = undefined;
    const r = this.store.getSnapshot().ringing;
    if (!r) return;
    this.store.update((s) => (s.ringing = null));
    if (missed) this.popUp({ id: "huddle-" + r.channel, channelID: r.channel, title: t("Missed huddle"), body: t("{name} called you.", { name: this.name(r.from) }) });
  }

  // answerCall joins the call one is being rung for, and opens its DM.
  answerCall() {
    const st = this.store.getSnapshot();
    const r = st.ringing;
    if (!r) return;
    // The caller gave up just now: answering would call them back.
    if (!st.huddles.has(r.channel)) return this.stopRinging(true);
    this.stopRinging();
    navigate(`/c/${r.channel}`);
    call.join(this, r.channel).catch((err) => this.popUp({ id: "huddle-" + r.channel, channelID: r.channel, title: t("Could not join the huddle"), body: err instanceof Error ? err.message : String(err) }));
  }

  // declineCall stops the ringing and tells the caller.
  declineCall() {
    const r = this.store.getSnapshot().ringing;
    if (!r) return;
    this.stopRinging();
    this.engine.declineHuddle(r.channel);
  }

  // callEnded says why one's DM call ended before anyone answered.
  callEnded(channel: UUID, why: "declined" | "no_answer", who?: UUID) {
    const title = why === "declined" && who ? t("{name} declined", { name: this.name(who) }) : t("No answer from {name}", { name: this.channelName(channel) });
    this.popUp({ id: "huddle-" + channel, channelID: channel, title, body: why === "declined" ? t("They can't join the huddle now.") : t("Nobody joined the huddle.") });
  }

  // popUp shows a pop-up for a while.
  popUp(toast: { id: string; channelID?: UUID; title: string; body: string }) {
    this.store.update((s) => {
      s.toasts = [...s.toasts.filter((x) => x.id !== toast.id), toast].slice(-4);
    });
    setTimeout(() => this.dismissToast(toast.id), 15_000);
  }

  // loadHuddles fetches the calls going on in one's channels, when the
  // server has huddles; their events keep them current.
  async loadHuddles() {
    const { huddles } = await this.api.features().catch(() => ({ huddles: false }));
    if (!huddles) return;
    const list = await this.api.huddles().then((r) => r.huddles, () => []);
    this.store.update((s) => {
      s.huddlesOn = true;
      s.huddles = new Map(list.map((h) => [h.channel_id, h]));
    });
  }

  // shown is the desktop notifications still up, by message, so a recalled
  // message's can be taken down.
  private shown = new Map<UUID, Notification>();

  // unnotify takes down what told of a message that was recalled.
  private unnotify(id: UUID) {
    this.shown.get(id)?.close();
    this.shown.delete(id);
    this.store.update((s) => {
      if (s.toasts.some((x) => x.messageID === id)) s.toasts = s.toasts.filter((x) => x.messageID !== id);
    });
  }

  // notify tells the person about a DM or mention: nothing if they are
  // looking at that channel, a desktop notification if the page is in the
  // background and they allowed them, and otherwise a pop-up in the page,
  // which also works where browsers allow no notifications (plain HTTP),
  // unless they turned pop-ups off.
  private notify(m: Message, channelID: UUID) {
    if (this.paused()) return;
    const front = document.visibilityState === "visible" && document.hasFocus();
    if (front && parse(location.pathname).channel === channelID) return;
    const cs = this.store.getSnapshot().channels.get(channelID);
    const dm = cs?.channel.kind === "dm" || cs?.channel.kind === "group_dm";
    const title = dm ? this.name(m.author.id) : t("{name} in {channel}", { name: this.name(m.author.id), channel: this.channelName(channelID) });
    const body = m.text.replace(/<@([0-9a-f-]{36})>/g, (_, id: string) => "@" + this.name(id)).slice(0, 200) || t("sent a file");
    chime();
    if (!front && typeof Notification !== "undefined" && Notification.permission === "granted") {
      const n = new Notification(title, { body, tag: m.id });
      this.shown.set(m.id, n);
      n.onclose = () => this.shown.delete(m.id);
      n.onclick = () => {
        window.focus();
        navigate(`/c/${channelID}/m/${m.id}`);
        n.close();
      };
      return;
    }
    if (!popUpsOn()) return;
    const toast = { id: m.id, channelID, messageID: m.id, title, body };
    this.store.update((s) => {
      s.toasts = [...s.toasts.filter((t) => t.id !== toast.id), toast].slice(-4);
    });
    // Gone after a while in view; one that came while away waits for them.
    const expire = () => {
      if (document.visibilityState !== "visible") return void setTimeout(expire, 2000);
      this.dismissToast(toast.id);
    };
    setTimeout(expire, 8000);
  }

  // onReminder tells the person a reminder came due, as a DM would, and
  // puts it at the top of their reminders.
  private async onReminder(r: Reminder) {
    await this.loadReminders();
    const full = this.store.getSnapshot().reminders?.find((x) => x.id === r.id) ?? r;
    if (this.paused()) return;
    const about = full.message;
    const body = (
      full.text || (about ? t("{name} in {channel}: {text}", { name: this.name(about.author.id), channel: this.channelName(about.channel_id), text: about.text }) : "")
    ).slice(0, 200);
    chime();
    const front = document.visibilityState === "visible" && document.hasFocus();
    const open = () => navigate(about ? `/c/${about.channel_id}/m/${about.id}` : "/inbox");
    if (!front && typeof Notification !== "undefined" && Notification.permission === "granted") {
      const n = new Notification(t("Reminder"), { body, tag: r.id });
      n.onclick = () => {
        window.focus();
        open();
        n.close();
      };
      return;
    }
    const toast = { id: r.id, channelID: about?.channel_id, messageID: about?.id, title: t("Reminder"), body };
    this.store.update((s) => {
      s.toasts = [...s.toasts.filter((t) => t.id !== toast.id), toast].slice(-4);
    });
  }

  // loadReminders fetches one's reminders not yet done.
  async loadReminders() {
    try {
      const { reminders } = await this.api.reminders();
      this.store.update((s) => (s.reminders = reminders));
    } catch {
      // The inbox shows them when it can.
    }
  }

  // remind sets a reminder, about a message or with text of its own.
  async remind(at: Date, about?: Message, text = "") {
    await this.api.createReminder({ channel_id: about?.channel_id, message_id: about?.id, text, remind_at: at.toISOString() });
    await this.loadReminders();
  }

  // loadScheduled fetches one's messages waiting to send.
  async loadScheduled() {
    try {
      const { scheduled } = await this.api.scheduled();
      this.store.update((s) => (s.scheduled = scheduled));
    } catch {
      // The composer and the Scheduled page show them when they can.
    }
  }

  // ---- your own profile ----

  async loadPreferences() {
    // A language picked on the sign-in page becomes the preference.
    const picked = takePicked();
    if (picked !== null && (await this.setLanguage(picked).then(() => true, () => false))) return;
    const { preferences } = await this.api.preferences().catch(() => ({ preferences: { notifications_paused_until: null, quiet_hours: null } }));
    this.setPreferences(preferences);
  }

  private setPreferences(p: Preferences) {
    // A language chosen on another device shows here too.
    if (p.language !== undefined && setChosen(p.language)) location.reload();
    // So do a theme and light or dark.
    if (p.theme !== undefined) fromAccount(p, (theme, a) => void this.api.setTheme(theme, a).catch(() => {}));
    if (p.text_size !== undefined) display(p);
    this.store.update((s) => {
      s.pausedUntil = p.notifications_paused_until;
      s.quietHours = p.quiet_hours ?? null;
      s.keywords = p.notify_keywords ?? [];
    });
  }

  // paused reports whether notifications are paused now: for a while, or
  // in quiet hours.
  paused(): boolean {
    return this.pausedFor() !== null;
  }

  // pausedFor says until when notifications are paused, and whether by
  // quiet hours, or is null when they are on.
  pausedFor(now = new Date()): { until: Date; quiet: boolean } | null {
    const st = this.store.getSnapshot();
    const until = st.pausedUntil ? new Date(st.pausedUntil) : null;
    if (until && until > now) return { until, quiet: false };
    const quiet = quietUntil(st.quietHours, now);
    return quiet && { until: quiet, quiet: true };
  }

  async setKeywords(keywords: string[]) {
    const { preferences } = await this.api.setKeywords(keywords);
    this.setPreferences(preferences);
  }

  async setLanguage(lang: string) {
    const { preferences } = await this.api.setLanguage(lang);
    this.setPreferences(preferences);
  }

  // setTheme shows a theme ("" for the workspace's) and light or dark (""
  // for the system's) at once, and keeps them for the account.
  async setTheme(theme: string, a: Appearance) {
    choose(theme, a);
    const { preferences } = await this.api.setTheme(theme, a);
    this.setPreferences(preferences);
  }

  // setDisplay shows a text size and density at once, and keeps them for
  // the account.
  async setDisplay(textSize: TextSize, density: Density, layout: Layout) {
    display({ text_size: textSize, density, message_layout: layout });
    const { preferences } = await this.api.setDisplay(textSize, density, layout);
    this.setPreferences(preferences);
  }

  async setQuietHours(q: QuietHours | null) {
    const { preferences } = await this.api.setQuietHours(q);
    this.setPreferences(preferences);
  }

  // refreshPrincipal takes someone as they are now, after they were made or
  // changed their name, status or away: as the server sent them, or looked
  // up; for oneself, from another device, also the pause.
  // lookups counts lookups of each person, so that only the latest one's
  // answer is taken: answers can come back out of order.
  private lookups = new Map<UUID, number>();

  async refreshPrincipal(id: UUID, given?: Principal) {
    const self = this.store.getSnapshot().me?.id === id;
    const n = (this.lookups.get(id) ?? 0) + 1;
    this.lookups.set(id, n);
    const { principal } = given && !self ? { principal: given } : await this.api.principal(id).catch(() => ({ principal: undefined }));
    if (!principal || this.lookups.get(id) !== n) return;
    this.directory.set(id, principal);
    if (self) await this.loadPreferences();
    this.store.update((s) => {
      if (self) s.me = principal;
      s.profileBumps++;
    });
  }

  // takePeople takes many people as they are now, redrawing once; oneself
  // is looked up, for the pause too.
  takePeople(ps: Principal[]) {
    const me = this.store.getSnapshot().me?.id;
    for (const p of ps) {
      if (p.id === me) void this.refreshPrincipal(p.id);
      else this.directory.set(p.id, p);
    }
    this.store.update((s) => s.profileBumps++);
  }

  // statusOf is a person's custom status, if it has not cleared.
  statusOf(id: UUID): { emoji: string; text: string } | null {
    const p = this.directory.get(id);
    if (!p || (!p.status_emoji && !p.status_text)) return null;
    if (p.status_expires_at && Date.parse(p.status_expires_at) <= Date.now()) return null;
    return { emoji: p.status_emoji ?? "", text: p.status_text ?? "" };
  }

  // setOwn changes one's own name, status or away and shows it at once.
  async setOwn(change: () => Promise<{ principal: Principal }>) {
    const { principal } = await change();
    // Newer than any lookup of oneself still on its way.
    this.lookups.set(principal.id, (this.lookups.get(principal.id) ?? 0) + 1);
    this.directory.set(principal.id, principal);
    this.store.update((s) => {
      s.me = principal;
      s.profileBumps++;
    });
  }

  async pause(until: Date | null) {
    const { preferences } = await this.api.pauseNotifications(until ? until.toISOString() : null);
    this.setPreferences(preferences);
  }

  dismissToast(id: string) {
    this.store.update((s) => {
      s.toasts = s.toasts.filter((t) => t.id !== id);
    });
  }

  // trackBadge counts mentions, plus unread messages in direct messages,
  // into the tab's title and the installed app's badge (ADR-0014). It also
  // trackThreads keeps the count of followed threads with unread replies:
  // now, after each reply to one, and when one is read here.
  private trackThreads() {
    let seen = -1;
    let timer: ReturnType<typeof setTimeout> | undefined;
    this.store.subscribe(() => {
      const n = this.store.getSnapshot().threadNotices;
      if (n === seen) return;
      seen = n;
      clearTimeout(timer);
      timer = setTimeout(() => void this.refreshUnreadThreads(), 300);
    });
    void this.refreshUnreadThreads();
  }

  async refreshUnreadThreads() {
    try {
      const { unread } = await this.api.unreadThreads();
      if (unread !== this.store.getSnapshot().unreadThreads) this.store.update((s) => (s.unreadThreads = unread));
    } catch {
      // The count comes back with the next reply.
    }
  }

  // loadBookmarks fetches a channel's bookmarks, which its events then
  // keep current.
  async loadBookmarks(channel: UUID) {
    const { bookmarks } = await this.api.bookmarks(channel);
    this.store.update((s) => s.bookmarks.set(channel, bookmarks));
  }

  // loadPins fetches a channel's pins, which its events then keep current.
  async loadPins(channel: UUID) {
    const { pins } = await this.api.pins(channel);
    this.store.update((s) => s.pins.set(channel, new Map(pins.map((p) => [p.message.id, p.pinned_by]))));
    return pins;
  }

  // recalledTexts is what one recalled in this tab, by message, so it can be
  // put back in the message box to send again; it is kept only in memory.
  private recalledTexts = new Map<UUID, string>();

  // recall takes back a message; the channel's event shows it recalled.
  async recall(m: Message) {
    if (m.text) this.recalledTexts.set(m.id, m.text);
    try {
      await this.api.recall(m.channel_id, m.id);
    } catch (err) {
      this.recalledTexts.delete(m.id);
      throw err;
    }
  }

  // recalledText is the text of a message one recalled here, if kept.
  recalledText(id: UUID): string | undefined {
    return this.recalledTexts.get(id);
  }

  // forgetRecalled drops it once it is back in a message box.
  forgetRecalled(id: UUID) {
    this.recalledTexts.delete(id);
  }

  // setPinned pins a message to its channel or unpins it; the channel's
  // event tells everyone, this one included.
  async setPinned(channel: UUID, message: UUID, pinned: boolean) {
    await this.api.setPinned(channel, message, pinned);
    const me = this.store.getSnapshot().me?.id;
    this.store.update((s) => {
      const pins = s.pins.get(channel);
      if (!pins || !me) return;
      if (pinned) pins.set(message, me);
      else pins.delete(message);
    });
  }

  // setClosed closes a DM for oneself, or opens it again. The server reads
  // it to its end as it closes; its channel.read follows.
  async setClosed(channel: UUID, closed: boolean) {
    const { closed_at } = await this.api.setClosed(channel, closed);
    this.store.update((s) => {
      const m = s.channels.get(channel)?.channel.membership;
      if (m) Object.assign(m, closed ? { closed_at, unread_count: 0, mention_count: 0 } : { closed_at });
    });
  }

  // clearHistory clears a DM's history for oneself; one's other devices
  // hear of it from membership.updated, and its channel.read follows.
  async clearHistory(channel: UUID) {
    const { cleared_seq } = await this.api.clearHistory(channel);
    this.store.update((s) => clearTo(s, channel, cleared_seq));
  }

  // setSaved saves a message for later or unsaves it, showing it at once.
  async setSaved(channel: UUID, message: UUID, saved: boolean) {
    this.store.update((s) => {
      if (saved) s.saved.add(message);
      else s.saved.delete(message);
    });
    try {
      await this.api.setSaved(channel, message, saved);
    } catch (err) {
      this.store.update((s) => {
        if (saved) s.saved.delete(message);
        else s.saved.add(message);
      });
      throw err;
    }
  }

  // markThreadRead records reading a thread up to seq, once per new seq.
  private threadRead = new Map<UUID, number>();
  markThreadRead(channel: UUID, root: UUID, seq: number) {
    if (!seq || (this.threadRead.get(root) ?? 0) >= seq) return;
    this.threadRead.set(root, seq);
    void this.api.markThreadRead(channel, root, seq).then(
      () => this.refreshUnreadThreads(),
      () => this.threadRead.delete(root),
    );
  }

  // loads the members of DMs that arrive while the page is open, so they
  // are named.
  private trackBadge() {
    const nav = navigator as Navigator & { setAppBadge?: (n: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
    let last = "";
    this.store.subscribe(() => {
      let n = 0;
      let unnamed = false;
      for (const cs of this.store.getSnapshot().channels.values()) {
        const dm = cs.channel.kind === "dm" || cs.channel.kind === "group_dm";
        if (dm && !this.dmMembers.has(cs.channel.id)) unnamed = true;
        n += waitingCount(cs.channel);
      }
      if (unnamed) void this.loadDMMembers().catch(() => {});
      // The title is the site's name, after how many wait.
      const title = n > 0 ? `(${n > 99 ? "99+" : n}) ${siteName()}` : siteName();
      if (title === last) return;
      last = title;
      document.title = title;
      if (n > 0) setFaviconDot(true);
      else clearFaviconDot();
      if (nav.setAppBadge) void (n > 0 ? nav.setAppBadge(n) : nav.clearAppBadge?.())?.catch(() => {});
    });
  }

  // trackPresence reports the user away after five minutes out of view.
  private trackPresence() {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const update = () => {
      clearTimeout(timer);
      if (document.visibilityState === "visible") this.engine.setPresence("active");
      else timer = setTimeout(() => this.engine.setPresence("away"), 5 * 60 * 1000);
    };
    document.addEventListener("visibilitychange", update);
    this.stopPresence = () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", update);
    };
  }

  private stopPresence = () => {};

  async start() {
    const me = await this.api.me();
    if (me.mfa_setup_required) throw new MFASetupNeeded();
    const { principal, workspace } = me;
    const { principals } = await this.api.principals({ status: "active" });
    // Into the directory there is, so lookups already asked for still land.
    for (const p of principals) this.directory.set(p.id, p);
    this.store.update((s) => {
      s.me = principal;
      s.workspace = workspace?.name ?? "";
      s.recallSeconds = workspace?.recall_seconds ?? s.recallSeconds;
      s.channelCreation = workspace?.channel_creation ?? s.channelCreation;
      s.drafts = draftedChannels(principal.id);
    });
    fromWorkspace(workspace?.theme);
    // The page's branding may be a few seconds old, or an offline copy's.
    void this.api.branding().then(
      ({ branding }) => {
        setBranding(branding);
        this.store.update(() => {});
      },
      () => {},
    );
    await this.loadPreferences();
    // People who never said their time zone have this browser's, so others
    // can see their local time.
    if (principal.kind === "user" && !principal.time_zone) void this.setOwn(() => this.api.updateMe({ time_zone: browserZone() })).catch(() => {});
    await this.engine.start();
    await this.loadDMMembers();
    this.trackThreads();
    void this.loadGroups();
    void this.loadEmoji();
    void this.loadHuddles();
    void this.loadReminders();
    void this.loadScheduled();
    void this.api.sidebar().then(
      (l) =>
        this.store.update((s) => {
          s.sidebar = l.sections;
          s.sidebarSort = l.sort;
          s.sidebarShow = l.show;
          s.sidebarShowArchived = !!l.show_archived;
        }),
      () => {},
    );
    void this.api.savedIDs().then(
      ({ ids }) => this.store.update((s) => (s.saved = new Set(ids))),
      () => {},
    );
    this.trackPresence();
    this.trackBadge();
    unlockSound();
  }

  // dmAsked are the DMs whose members were asked for, so each is asked once.
  private dmAsked = new Set<UUID>();

  async loadDMMembers() {
    const dms = [...this.store.getSnapshot().channels.values()].filter(
      (c) => (c.channel.kind === "dm" || c.channel.kind === "group_dm") && !this.dmMembers.has(c.channel.id) && !this.dmAsked.has(c.channel.id),
    );
    if (dms.length === 0) return;
    dms.forEach((c) => this.dmAsked.add(c.channel.id));
    await Promise.allSettled(
      dms.map(async (c) => {
        const { members } = await this.api.members(c.channel.id);
        this.dmMembers.set(c.channel.id, members);
      }),
    );
    this.store.update(() => {});
    // Presence is shown for DM partners, so those are the people watched.
    const me = this.store.getSnapshot().me?.id;
    // And their own, for the dot on the account button.
    this.engine.watchPresence([...(me ? [me] : []), ...[...this.dmMembers.values()].flat().filter((id) => id !== me)]);
  }

  private fileURLs = new Map<UUID, { url: string; until: number }>();

  // fileURL returns a signed link to a file, reusing one until shortly
  // before it expires.
  async fileURL(id: UUID): Promise<string> {
    const cached = this.fileURLs.get(id);
    if (cached && cached.until > Date.now()) return cached.url;
    const { url } = await this.api.file(id);
    this.fileURLs.set(id, { url, until: Date.now() + 50 * 60 * 1000 });
    return url;
  }

  private shortcutLists = new Map<UUID, { list: Promise<Shortcut[]>; until: number }>();

  // shortcuts are the workflows a channel's messages offer, fetched once a
  // minute per channel.
  shortcuts(channel: UUID): Promise<Shortcut[]> {
    const cached = this.shortcutLists.get(channel);
    if (cached && cached.until > Date.now()) return cached.list;
    const list = this.api.shortcuts(channel).then(
      (r) => r.shortcuts,
      () => [],
    );
    this.shortcutLists.set(channel, { list, until: Date.now() + 60_000 });
    return list;
  }

  private caps?: Promise<Map<string, Capability>>;
  private cmds?: Promise<{ command: string; app_name: string; description: string; usage_hint: string }[]>;

  // commands returns the workspace's slash commands, fetched once.
  // features are the model tiers the server has, fetched once.
  private feats?: Promise<Record<string, boolean>>;
  features() {
    this.feats ??= this.api.features().then(
      (r) => r.tiers,
      () => {
        this.feats = undefined;
        return {};
      },
    );
    return this.feats;
  }

  commands() {
    this.cmds ??= this.api.commands().then(
      (r) => r.commands,
      () => {
        this.cmds = undefined;
        return [];
      },
    );
    return this.cmds;
  }

  // capability returns a capability card, for the forms its tasks need.
  async capability(name: string, retry = true): Promise<Capability | undefined> {
    this.caps ??= this.api
      .capabilities()
      .then(({ capabilities }) => new Map(capabilities.map((c) => [c.name, c])))
      .catch(() => {
        this.caps = undefined;
        return new Map<string, Capability>();
      });
    const found = (await this.caps).get(name);
    if (found || !retry) return found;
    // Registered since the list was fetched, as built-in capabilities are.
    this.caps = undefined;
    return this.capability(name, false);
  }

  name(id: UUID): string {
    return this.directory.get(id)?.display_name ?? t("unknown");
  }

  // channelName is a channel's name, or the other members' names for a DM.
  channelName(id: UUID): string {
    const cs = this.store.getSnapshot().channels.get(id);
    if (!cs) return "";
    if (cs.channel.name) return "#" + cs.channel.name;
    const me = this.store.getSnapshot().me?.id;
    const others = (this.dmMembers.get(id) ?? []).filter((m) => m !== me);
    return others.length === 0 ? t("{name} (you)", { name: this.name(me ?? "") }) : others.map((m) => this.name(m)).join(t(", "));
  }

  // mentionsIn turns @handle into Lantern's <@principal_id> mentions, and a
  // user group's @handle into Slack's <!subteam^id|@handle>; a person's
  // handle wins over a group's.
  mentionsIn(text: string): string {
    const byHandle = new Map([...this.directory.values()].map((p) => [p.handle, p.id]));
    const groups = new Map(this.store.getSnapshot().groups.map((g) => [g.handle, g.id]));
    return text.replace(/(^|\s)@([a-z0-9][a-z0-9._-]*)/g, (m, pre: string, handle: string) => {
      const id = byHandle.get(handle);
      if (id) return `${pre}<@${id}>`;
      const g = groups.get(handle);
      return g ? `${pre}<!subteam^${g}|@${handle}>` : m;
    });
  }

  // arrangeSidebar changes the sidebar's sections at once and saves them;
  // if saving fails they go back to what was saved.
  arrangeSidebar(change: (sections: SidebarSection[]) => SidebarSection[]) {
    const before = this.store.getSnapshot().sidebar;
    const next = change(before);
    if (next !== before) this.saveSidebar({ sections: next });
  }

  // sidebarOptions sets how the sidebar sorts and what it shows.
  sidebarOptions(o: Pick<Partial<SidebarLayout>, "sort" | "show" | "show_archived">) {
    this.saveSidebar(o);
  }

  private sidebarSaving = Promise.resolve();
  private saveSidebar(change: Partial<SidebarLayout>) {
    const st = this.store.getSnapshot();
    const before: SidebarLayout = { sections: st.sidebar, sort: st.sidebarSort, show: st.sidebarShow, show_archived: st.sidebarShowArchived };
    const next = { ...before, ...change };
    const apply = (l: SidebarLayout) =>
      this.store.update((s) => {
        s.sidebar = l.sections;
        s.sidebarSort = l.sort;
        s.sidebarShow = l.show;
        s.sidebarShowArchived = !!l.show_archived;
      });
    apply(next);
    // One save at a time, in order, so the last change is the one kept.
    this.sidebarSaving = this.sidebarSaving.then(() =>
      this.api.setSidebar(next).then(
        () => {},
        () => {
          if (this.store.getSnapshot().sidebar === next.sections) apply(before);
        },
      ),
    );
  }

  // linkedMessage looks up a message a link points to, once: null if one
  // cannot read it.
  private linked = new Map<string, Promise<Message | null>>();
  linkedMessage(channel: UUID, id: UUID): Promise<Message | null> {
    let p = this.linked.get(id);
    if (!p) {
      p = this.api.message(channel, id).then(
        (r) => r.message,
        () => null,
      );
      this.linked.set(id, p);
    }
    return p;
  }

  // loadEmoji fetches the workspace's custom emoji.
  async loadEmoji() {
    try {
      const { emoji } = await this.api.customEmoji();
      setCustom(emoji.map((e) => e.name));
      this.store.update((s) => (s.emoji = emoji));
    } catch {
      // Custom emoji show as :name: until they load.
    }
  }

  private emojiImages = new Map<string, Promise<string>>();

  private previews = new Map<string, Promise<LinkPreview[]>>();

  // linkPreviews previews the links in a message, asked for once per
  // version of its text.
  linkPreviews(m: Message): Promise<LinkPreview[]> {
    const key = `${m.id}@${m.edited_at ?? ""}`;
    let p = this.previews.get(key);
    if (!p) {
      p = this.api.linkPreviews(m.channel_id, m.id).then(
        (r) => r.previews,
        () => [],
      );
      this.previews.set(key, p);
    }
    return p;
  }

  private avatars = new Map<string, Promise<string>>();

  // avatarURL is someone's profile photo as a URL to show, fetched once per
  // version of it; undefined when they have none.
  avatarURL(id: UUID): Promise<string> | undefined {
    const at = this.directory.get(id)?.avatar_at;
    if (!at) return undefined;
    const key = `${id}@${at}`;
    let url = this.avatars.get(key);
    if (!url) {
      url = this.api.avatarImage(id).then((b) => URL.createObjectURL(b));
      url.catch(() => this.avatars.delete(key));
      this.avatars.set(key, url);
    }
    return url;
  }

  // emojiURL is a custom emoji's image as a URL to show, fetched once per
  // version of it: one removed and added again is fetched again.
  emojiURL(name: string): Promise<string> | undefined {
    const e = this.store.getSnapshot().emoji.find((x) => x.name === name);
    if (!e) return undefined;
    const key = `${name}@${e.created_at}`;
    let url = this.emojiImages.get(key);
    if (!url) {
      url = this.api.emojiImage(name).then((b) => URL.createObjectURL(b));
      url.catch(() => this.emojiImages.delete(key));
      this.emojiImages.set(key, url);
    }
    return url;
  }

  // loadGroups fetches the workspace's user groups.
  async loadGroups() {
    try {
      const { groups } = await this.api.groups();
      this.store.update((s) => (s.groups = groups));
    } catch {
      // Mentions of groups still show by the handle written in them.
    }
  }

  // A mention of a group not fetched yet fetches the groups again, once
  // per group: one made since, or one since deleted.
  private groupsAsked = new Set<UUID>();

  // groupMention describes a group mention: its handle, and whether one is
  // in the group.
  groupMention(id: UUID): { handle: string; mine: boolean } | undefined {
    const st = this.store.getSnapshot();
    const g = st.groups.find((x) => x.id === id);
    // A group made since they were fetched: fetch them again.
    if (!g && !this.groupsAsked.has(id)) {
      this.groupsAsked.add(id);
      void this.loadGroups();
    }
    return g && { handle: g.handle, mine: !!st.me && g.member_ids.includes(st.me.id) };
  }

  stop() {
    this.unwatchCall();
    this.stopRinging();
    void call.leaveFor(this);
    this.stopPresence();
    this.engine.stop();
  }
}

// browserSocket adapts a WebSocket to the engine's SocketLike.
function browserSocket(url: string, protocol: string): SocketLike {
  const ws = new WebSocket(url, protocol);
  const s: SocketLike = {
    send: (data) => ws.send(data),
    close: (code, reason) => ws.close(code, reason),
    onopen: null,
    onmessage: null,
    onclose: null,
  };
  ws.onopen = () => s.onopen?.();
  ws.onmessage = (e) => s.onmessage?.({ data: String(e.data) });
  ws.onclose = (e) => s.onclose?.({ code: e.code });
  return s;
}
